import {createAsyncThunk} from '@reduxjs/toolkit';
import createBlackouts from 'api/blackouts/create-blackouts';
import deleteBlackout from 'api/blackouts/delete-blackout';
import updateBlackout from 'api/blackouts/update-blackout';
import getBlackouts from 'api/blackouts/get-blackouts';

/** @namespace Blackouts.Actions */

/**
 * Thunk that fetches all blackouts .
 */
export const getAllBlackouts = createAsyncThunk(
    'blackouts/getAllBlackouts',
    /**
     * @param {Number} page
     * @param {Array<Number>} facilityIds
     * @param {string} blackoutStatus
     * @param {string} dateFrom
     * @param {string} dateTo
     * @param {function} handleAbortSignal
     * @param {object} abortSignal
     * @param rejectWithValue
     * @param signal
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|TBlackoutsResponse[]>}
     */ async (
        {
            page,
            facilityIds,
            blackoutStatus,
            dateFrom,
            dateTo,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getBlackouts(
                {
                    page,
                    facilityIDs: facilityIds,
                    blackoutStatus,
                    dateFrom,
                    dateTo,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that deletes a facility blackout.
 */
export const deleteFacilityBlackout = createAsyncThunk(
    'blackouts/deleteBlackout',
    /**
     * @param {Object} params - The parameters for deleting a blackout
     * @param {Number} params.facilityId
     * @param {Number} params.blackoutId
     * @param rejectWithValue
     */
    async ({facilityId, blackoutId}, {rejectWithValue}) => {
        try {
            await deleteBlackout(facilityId, blackoutId);

            return blackoutId;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that creates a collection of facility blackouts.
 */
export const createFacilityBlackouts = createAsyncThunk(
    'blackouts/createBlackout',
    /**
     * @param {TCreateBlackout} blackout
     * @param {function} handleAbortSignal
     * @param {object} abortSignal
     * @param rejectWithValue
     * @param signal
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|TBlackoutsResponse[]>}
     */ async ({facilityId, blackouts}, {rejectWithValue}) => {
        try {
            const response = await createBlackouts({facilityId, blackouts});

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that updates a facility blackout.
 */
export const updateFacilityBlackout = createAsyncThunk(
    'blackouts/updateBlackout',
    /**
     * @param {Object} params - The parameters for updating a blackout
     * @param {Number} params.facilityId - The id of the facility the blackout belongs to
     * @param {Number} params.blackoutId - The id of the blackout to update
     * @param {Object} params.blackout - The updated blackout data
     * @param rejectWithValue
     */ async ({facilityId, blackoutId, blackout}, {rejectWithValue}) => {
        try {
            const response = await updateBlackout({
                facilityId,
                blackoutId,
                blackout,
            });

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
